import { useCallback, useRef, useState } from 'react'
import { ApiError } from '@/lib/api'
import { confirmPayment, type ConfirmOutcome } from '@/features/payments/confirm'
import type { GatewayResult } from '@/features/payments/gateways'
import type { PaymentConfirmStatus, PaymentValidateResult } from '@/types/models'

export type CheckoutPhase = 'idle' | 'preparing' | 'gateway' | 'confirming' | 'success' | 'processing' | 'failed' | 'cancelled' | 'unconfirmed'

/** `onCancel` fires for a dismissed or failed gateway so the server side can be released. */
export interface CheckoutFlow<T> {
  start: () => Promise<T>
  openGateway: (order: T) => Promise<GatewayResult>
  /** Omit for flows without a backend confirm (free plan). */
  confirm?: (order: T) => { validate: () => Promise<PaymentValidateResult>; status: () => Promise<PaymentConfirmStatus>; deadlineMs?: number }
  onCancel?: (order: T, result: GatewayResult) => Promise<void>
  /** Gateway said "processing" (Cashfree modal closed): treat as needing confirmation. */
  onSuccess?: (order: T) => Promise<unknown> | void
}

export interface CheckoutState {
  phase: CheckoutPhase
  error: string | null
  /** Seconds left in the current confirm window. */
  countdown: number | null
}

export interface UseCheckout extends CheckoutState {
  run: () => Promise<CheckoutPhase>
  reset: () => void
  isBusy: boolean
}

export function useCheckout<T>(flow: CheckoutFlow<T>): UseCheckout {
  const [state, setState] = useState<CheckoutState>({ phase: 'idle', error: null, countdown: null })
  const flowRef = useRef(flow)
  flowRef.current = flow
  const abort = useRef<AbortController | null>(null)

  const finish = (phase: CheckoutPhase, error: string | null = null) => {
    setState({ phase, error, countdown: null })
    return phase
  }

  const run = useCallback(async (): Promise<CheckoutPhase> => {
    const f = flowRef.current
    abort.current?.abort()
    const ac = new AbortController()
    abort.current = ac
    setState({ phase: 'preparing', error: null, countdown: null })
    let order: T
    try {
      order = await f.start()
    } catch (e) {
      return finish('failed', e instanceof ApiError ? e.message : 'Something went wrong while starting the payment.')
    }
    setState((s) => ({ ...s, phase: 'gateway' }))
    let result: GatewayResult
    try {
      result = await f.openGateway(order)
    } catch (e) {
      result = { outcome: 'failed', message: e instanceof Error ? e.message : undefined }
    }
    if (result.outcome === 'cancelled' || result.outcome === 'failed') {
      try {
        await f.onCancel?.(order, result)
      } catch {
        // Releasing the server side is best effort; the reconciler cleans up anyway.
      }
      return finish(result.outcome, result.message ?? null)
    }
    if (!f.confirm) {
      await f.onSuccess?.(order)
      return finish('success')
    }
    setState((s) => ({ ...s, phase: 'confirming' }))
    const c = f.confirm(order)
    let outcome: ConfirmOutcome
    try {
      outcome = await confirmPayment({ ...c, signal: ac.signal, onCountdown: (seconds) => setState((s) => ({ ...s, countdown: seconds })) })
    } catch (e) {
      return finish('unconfirmed', e instanceof ApiError ? e.message : null)
    }
    if (outcome === 'confirmed') {
      await f.onSuccess?.(order)
      return finish('success')
    }
    if (outcome === 'timeout') return finish('failed')
    if (outcome === 'aborted') return finish('idle')
    return finish('unconfirmed')
  }, [])

  const reset = useCallback(() => {
    abort.current?.abort()
    setState({ phase: 'idle', error: null, countdown: null })
  }, [])

  return { ...state, run, reset, isBusy: state.phase === 'preparing' || state.phase === 'gateway' || state.phase === 'confirming' }
}
